import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PrimaryButton from "../buttons/primaryButton";
import AlertBanner from "../shared/AlertBanner";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import type { AppliedVoucher } from "../hooks/useVoucherRedemption";

interface VoucherRedemptionProps {
  readonly appliedVouchers: readonly AppliedVoucher[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly currency: string;
  readonly countryCode: string;
  readonly onApply: (code: string) => void;
  readonly onRemove: (code: string) => void;
}

/**
 * Soda voucher redemption UI. Presentational only — the redemption state and
 * business logic live in `useVoucherRedemption` (owned by the payment step).
 *
 * Lets the guest enter a voucher code, apply it, and remove previously applied
 * vouchers. Validation/status feedback is announced via `AlertBanner`.
 */
const VoucherRedemption: React.FC<VoucherRedemptionProps> = ({
  appliedVouchers,
  loading,
  error,
  currency,
  countryCode,
  onApply,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");

  const handleApply = () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    onApply(trimmed);
    setCode("");
  };

  return (
    <div
      id="gc-voucher-redemption"
      className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-white gcw:p-bs-3 gcw:mb-bs-3"
    >
      <label htmlFor="voucher-code" className="gcw:block gcw:text-body gcw:font-medium gcw:text-text-primary gcw:mb-1">
        {t("booking_voucher_code")}
      </label>
      <div className="gcw:flex gcw:gap-2">
        <input
          type="text"
          id="voucher-code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          className="container-inputs gcw:flex-1"
          placeholder={t("booking_voucher_placeholder")}
          aria-describedby="voucher-status"
          disabled={loading}
        />
        <PrimaryButton
          text={t("booking_voucher_apply")}
          type="button"
          onClick={handleApply}
          loading={loading}
          disabled={code.trim().length === 0}
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
          ariaLabel={t("booking_voucher_apply")}
        />
      </div>

      <div id="voucher-status" aria-live="polite">
        {error && (
          <AlertBanner variant="danger" className="gcw:mt-2" textClassName="gcw:text-sm">
            {error}
          </AlertBanner>
        )}
      </div>

      {appliedVouchers.length > 0 && (
        <ul id="gc-voucher-list" className="gcw:mt-bs-3 gcw:flex gcw:flex-col gcw:gap-2">
          {appliedVouchers.map((voucher) => (
            <li
              key={voucher.code}
              className="gcw:flex gcw:items-center gcw:justify-between gcw:rounded-md gcw:bg-bg-surface gcw:px-bs-3 gcw:py-2 gcw:text-body gcw:text-text-secondary"
            >
              <span>
                {voucher.code}
                <span className="gcw:ml-2 gcw:font-semibold gcw:text-text-primary">
                  {formatCurrency(voucher.amountToRedeem / 100, currency, countryCode)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => onRemove(voucher.code)}
                disabled={loading}
                className="gcw:text-sm gcw:font-medium gcw:text-primary gcw:underline gcw:hover:text-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:cursor-not-allowed gcw:disabled:opacity-50"
                aria-label={t("booking_voucher_remove_aria", "Remove voucher {{code}}", { code: voucher.code })}
              >
                {t("booking_voucher_remove", "Remove")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VoucherRedemption;
